import { BehaviorSubject, map } from 'rxjs';
const EDITABLE_CELL_BG = '#dbeafe';
const TOKEN_CELL_BG = '#FFFF00';

export type ComponentType =
  | 'LABEL'
  | 'TEXT'
  | 'FORMULA'
  | 'NUMBER'
  | 'DATETIME'
  | 'LONGLABEL'
  | 'LONGTEXT'
  | 'HEADER'
  | 'HEADERTEXT'
  | 'VALUE'
  | 'COMPANY'
  | 'COUNTRY'
  | 'CURRENCY'
  | 'COMPANYDROPDOWN';

interface ThemeDef {
  bgColor?: string;
  defaultValue?: string | number;
  numberFormat?: string;
  comment?: string;
  editable?: boolean;
  validation?: 'number' | 'datetime';
}

export const univerTheme: Record<ComponentType, ThemeDef> = {
  LABEL:      {},
  TEXT:       { bgColor: EDITABLE_CELL_BG, editable: true },
  FORMULA:    { defaultValue: '=' },
  NUMBER:     { bgColor: EDITABLE_CELL_BG, defaultValue: 'No Default', editable: true },
  DATETIME:   { bgColor: EDITABLE_CELL_BG, defaultValue: 'Auto Update', numberFormat: 'dd/mm/yyyy', editable: true, validation: 'datetime' },
  LONGLABEL:  {},
  LONGTEXT:   { bgColor: EDITABLE_CELL_BG, editable: true },
  HEADER:     { comment: 'HEADER' },
  HEADERTEXT: { bgColor: EDITABLE_CELL_BG, comment: 'HEADERTEXT', editable: true },
  VALUE:      { defaultValue: '=' },
  COMPANY:    { bgColor: TOKEN_CELL_BG, defaultValue: '{COMPANY}' },
  COUNTRY:    { bgColor: TOKEN_CELL_BG, defaultValue: '{COUNTRY}' },
  CURRENCY:   { bgColor: TOKEN_CELL_BG, defaultValue: '{CURRENCY}' },
  COMPANYDROPDOWN: { bgColor: TOKEN_CELL_BG, defaultValue: '{COMPANYDROPDOWN}' },
};

export const EDITABLE_COMPONENTS = new Set<string>(
  (Object.entries(univerTheme) as [ComponentType, ThemeDef][])
    .filter(([, def]) => def.editable)
    .map(([type]) => type),
);

// Components whose `value` is literal text, so a leading '=' is content rather than a formula.
export const TEXT_COMPONENTS = new Set<string>(['LABEL', 'LONGLABEL', 'HEADER', 'HEADERTEXT', 'TEXT', 'LONGTEXT']);

export const LOCKED_COMPONENTS = new Set<string>(['COMPANY', 'COUNTRY', 'CURRENCY', 'COMPANYDROPDOWN', 'DATETIME']);

const CWK_VALUE_COLUMN = 6;
// Kept beside CWK_VALUE_COLUMN so moving the column updates the message the editor shows.
export const CWK_VALUE_COLUMN_LABEL = 'G';
export const CWK_ACCOUNT_COLUMN = 1;
// Journal debit / credit. On a posting-account row the backend overwrites these at journal load
// (company_report_manager.py run_load), so a template formula there is destroyed and never restored.
export const CWK_DR_COLUMN = 3;
export const CWK_CR_COLUMN = 4;
const COMPANY_COLUMN = 0;

let _editorMode: 'rpk' | 'cwk' = 'rpk';

// Toolbar buttons need to react to the loaded type, and menu items are registered once at Univer
// creation - so the mode is published as well as stored.
const _editorMode$ = new BehaviorSubject<'rpk' | 'cwk'>(_editorMode);

/** Editor mode as observables, for menu items that hide on one template type. */
export const isCwk$ = _editorMode$.pipe(map((mode) => mode === 'cwk'));
export const isRpk$ = _editorMode$.pipe(map((mode) => mode !== 'cwk'));

export function setEditorMode(mode: 'rpk' | 'cwk'): void {
  _editorMode = mode;
  _editorMode$.next(mode);
}

export function getEditorMode(): 'rpk' | 'cwk' {
  return _editorMode;
}

// 'BS#', 'PL#' and '_cwk' are layout anchors, not postable accounts, and keep their roll-up formulas.
function isPostingAccountCode(code: string): boolean {
  return /^\d/.test(code);
}

/** Col B claims the row for the journal - an account code or a translation both count. */
export function columnBClaimsRow(fWorksheet: any, row: number): boolean {
  if (_editorMode !== 'cwk') return false;
  const cell = fWorksheet?.getRange?.(row, CWK_ACCOUNT_COLUMN);
  if (!cell) return false;
  const component = cell.getCustomMetaData()?.component;
  if (component && component !== 'LABEL') return false;
  const raw = String(cell.getValue() ?? '');
  const commaIndex = raw.indexOf(',');
  const code = (commaIndex === -1 ? raw : raw.slice(0, commaIndex)).trim();
  const translation = commaIndex === -1 ? '' : raw.slice(commaIndex + 1).trim();
  return isPostingAccountCode(code) || translation !== '';
}

/** The FORMULA tag, or literal '=' text a paste left behind before it could be tagged. */
export function cellHoldsFormula(cell: any): boolean {
  if (!cell) return false;
  if (cell.getCustomMetaData()?.component === 'FORMULA') return true;
  const text = String(cell.getFormula() || cell.getValue() || '').trim();
  return text.startsWith('=') && text.length > 1;
}

/** DR or CR column on `row` holding a formula, or -1. */
export function drCrFormulaColumn(fWorksheet: any, row: number): number {
  if (_editorMode !== 'cwk') return -1;
  for (const col of [CWK_DR_COLUMN, CWK_CR_COLUMN]) {
    if (cellHoldsFormula(fWorksheet?.getRange?.(row, col))) return col;
  }
  return -1;
}

export function drCrLabel(col: number): string {
  return col === CWK_DR_COLUMN ? 'D (DR)' : 'E (CR)';
}

/** Row-scoped companion to isValidPlacement, which sees only a column. */
export function isRowPlacementAllowed(
  fWorksheet: any,
  row: number,
  col: number,
  component: ComponentType,
): boolean {
  if (component !== 'FORMULA') return true;
  if (col !== CWK_DR_COLUMN && col !== CWK_CR_COLUMN) return true;
  return !columnBClaimsRow(fWorksheet, row);
}

export function isValidPlacement(component: ComponentType, col: number): boolean {
  if (component === 'COMPANY' && col !== COMPANY_COLUMN) return false;
  if (_editorMode === 'cwk') {
    if (col === CWK_VALUE_COLUMN) return component === 'VALUE';
    if (col === CWK_ACCOUNT_COLUMN) return component === 'LABEL';
    return component !== 'VALUE';
  }
  return component !== 'VALUE';
}

let _numberFormatWarned = false;

// Univer's numfmt command can fail on FUniver-created workbooks; swallow + warn once.
export function safeSetNumberFormat(fRange: any, pattern: string): void {
  try {
    fRange.setNumberFormat(pattern);
  } catch (err) {
    if (!_numberFormatWarned) {
      _numberFormatWarned = true;
      console.warn(`setNumberFormat failed (e.g. pattern "${pattern}"). Further failures are suppressed.`, err);
    }
  }
}

// NUMBER cells have no in-cell dropdown; clicking one opens the Set Number Options popup
// (default value + percentage / decimal-point formatting). 'No Default' is the resting label;
// the legacy 'Specific value' sentinel is still recognised when normalising older data.
export const NUMBER_NO_DEFAULT = 'No Default';
export const NUMBER_SPECIFIC_SENTINEL = 'Specific value';

// Highest decimal places allowed for NUMBER formatting; mirrors the editor's numfmt guard.
export const MAX_DECIMAL_PLACES = 4;

// Build a Univer number-format pattern from percentage / decimal settings.
// Shape ('0', '0.00', '0%', '0.00%') matches how snapshot-builder parses it back.
// Returns null for "no format" (auto / general).
export function buildNumberPattern(
  isPercentage: boolean,
  decimalPlaces: number | 'auto' | null,
): string | null {
  const hasDecimals = typeof decimalPlaces === 'number' && Number.isInteger(decimalPlaces);
  if (!hasDecimals && !isPercentage) return null;
  let fmt = '0';
  if (hasDecimals && (decimalPlaces as number) > 0) {
    fmt += '.' + '0'.repeat(Math.min(decimalPlaces as number, MAX_DECIMAL_PLACES));
  }
  if (isPercentage) fmt += '%';
  return fmt;
}

export const DATETIME_OPTIONS = ['Auto Update', 'Current Date', 'Specific date'];
export const DATETIME_SPECIFIC_SENTINEL = 'Specific date';

export function buildDateTimeRule(univerAPI: any) {
  return univerAPI.newDataValidation()
    .requireValueInList(DATETIME_OPTIONS)
    .setAllowInvalid(true)
    // A specific/current date value isn't one of the list options, so suppress the same
    // "Input must fall within specified range" invalid-cell tooltip on hover.
    .setOptions({ allowBlank: true, showErrorMessage: false })
    .build();
}

export function applyBulkRules(
  univerAPI: any,
  fWorkbook: any,
  cellRefs: Array<{ sheetId: string; row: number; col: number; component: string }>,
): void {
  const groups = new Map<string, { sheetId: string; validation: string; cells: Array<{ row: number; col: number }> }>();
  for (const ref of cellRefs) {
    const theme = univerTheme[ref.component as ComponentType];
    if (!theme?.validation) continue;
    const key = `${ref.sheetId}:${theme.validation}`;
    let group = groups.get(key);
    if (!group) {
      group = { sheetId: ref.sheetId, validation: theme.validation, cells: [] };
      groups.set(key, group);
    }
    group.cells.push({ row: ref.row, col: ref.col });
  }

  for (const { sheetId, validation, cells } of groups.values()) {
    const fWorksheet = fWorkbook.getSheetBySheetId(sheetId);
    if (!fWorksheet || cells.length === 0) continue;

    const buildRule = validation === 'datetime' ? buildDateTimeRule : null;
    if (!buildRule) continue;

    const fRanges = cells.map((c) => fWorksheet.getRange(c.row, c.col));
    fRanges[0].setDataValidation(buildRule(univerAPI));
    const registered = fRanges[0].getDataValidation();
    if (registered && fRanges.length > 1) {
      registered.setRanges(fRanges);
    }
  }
}

export function clearRules(fRange: any): void {
  try { fRange.setDataValidation(null); } catch { /* noop */ }
}

export function applyComponent(
  univerAPI: any,
  fRange: any,
  componentType: ComponentType,
  preserveValue = false,
  bypassPlacement = false,
): void {
  const theme = univerTheme[componentType];
  if (!theme) return;

  const range = fRange.getRange ? fRange.getRange() : fRange;
  const sheetId = fRange.getSheetId?.();
  const fWorksheet = sheetId
    ? univerAPI.getActiveWorkbook()?.getSheetBySheetId(sheetId)
    : univerAPI.getActiveWorkbook()?.getActiveSheet();
  if (!fWorksheet) return;

  for (let r = range.startRow; r <= range.endRow; r++) {
    for (let c = range.startColumn; c <= range.endColumn; c++) {
      if (!bypassPlacement && !isValidPlacement(componentType, c)) continue;
      if (!bypassPlacement && !isRowPlacementAllowed(fWorksheet, r, c, componentType)) continue;
      const cell = fWorksheet.getRange(r, c);
      cell.setCustomMetaData({ component: componentType });
      if (theme.bgColor) cell.setBackgroundColor(theme.bgColor);
      if (theme.numberFormat) safeSetNumberFormat(cell, theme.numberFormat);
      if (!preserveValue && theme.defaultValue !== undefined) cell.setValue(theme.defaultValue);
      if (theme.comment) {
        cell.clearCommentsAsync().then(() => {
          const richText = univerAPI.newRichText().insertText(theme.comment);
          cell.addCommentAsync(univerAPI.newTheadComment().setContent(richText));
        }).catch((e: any) => {
          console.error("Failed to update comment:", e);
        });
      }
      if (theme.validation === 'datetime') cell.setDataValidation(buildDateTimeRule(univerAPI));
    }
  }
}
